import type { CartItem, CartSnapshot } from "@/api/cart"

const DEFAULT_STORE_NAME = "Triibo Store"
const SLUG_FALLBACK_PREFIX = "produto"

const HTML_NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
}

const decodeHtmlEntities = (value: string): string => {
  return value.replace(/&(#x?[\da-f]+|[a-z]+);/gi, (entity, token: string) => {
    if (token.startsWith("#x") || token.startsWith("#X")) {
      const codePoint = Number.parseInt(token.slice(2), 16)

      return Number.isNaN(codePoint) ? entity : String.fromCodePoint(codePoint)
    }

    if (token.startsWith("#")) {
      const codePoint = Number.parseInt(token.slice(1), 10)

      return Number.isNaN(codePoint) ? entity : String.fromCodePoint(codePoint)
    }

    return HTML_NAMED_ENTITIES[token.toLowerCase()] ?? entity
  })
}

const toSlug = (value: string): string => {
  const normalized = decodeHtmlEntities(value)
    .replace(/[ª]/g, "a")
    .replace(/[º]/g, "o")
    .normalize("NFD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")

  return normalized.length > 0 ? normalized : SLUG_FALLBACK_PREFIX
}

const resolveCartItemSlug = (item: CartItem): string => {
  const slug = item.slug?.trim()

  if (slug) {
    return slug
  }

  return toSlug(item.name)
}

export const toCartPageItems = (snapshot: CartSnapshot) => {
  return snapshot.items.map((item) => {
    return {
      id: item.key,
      name: item.name,
      slug: resolveCartItemSlug(item),
      price: item.price,
      quantity: item.quantity,
      store: item.store || DEFAULT_STORE_NAME,
    }
  })
}

const toOptimisticTotal = (subtotal: number, discountTotal: number): number => {
  return Math.max(0, subtotal - discountTotal)
}

const recalculateSnapshot = (snapshot: CartSnapshot): CartSnapshot => {
  const subtotal = snapshot.items.reduce(
    (accumulator, item) => accumulator + item.lineTotal,
    0
  )
  const cartCount = snapshot.items.reduce(
    (accumulator, item) => accumulator + item.quantity,
    0
  )

  return {
    ...snapshot,
    cartCount,
    totals: {
      ...snapshot.totals,
      subtotal,
      total: toOptimisticTotal(subtotal, snapshot.totals.discountTotal),
    },
  }
}

export const applyQuantityOptimisticChange = (
  snapshot: CartSnapshot,
  key: string,
  quantity: number
): CartSnapshot => {
  return recalculateSnapshot({
    ...snapshot,
    items: snapshot.items.map((item) => {
      if (item.key !== key) {
        return item
      }

      return {
        ...item,
        quantity,
        lineTotal: item.price * quantity,
      }
    }),
  })
}

export const applyRemoveOptimisticChange = (
  snapshot: CartSnapshot,
  key: string
): CartSnapshot => {
  return recalculateSnapshot({
    ...snapshot,
    items: snapshot.items.filter((item) => item.key !== key),
  })
}
